import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { GripVertical, Trash2, RefreshCw, Sparkles, Mic, Eye, Code, Pencil, Clock, AlertTriangle } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Question } from '../../types/exam-config.types';
import { Tag } from '../ui/atoms/Tag';
import { SkillChip } from '../ui/atoms/SkillChip';
import { Button } from '@/components/ui/atoms/Button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/atoms/Input';
import { MonacoIDE } from '../interview/MonacoIDE';
import { SkeletonQuestionCard } from './SkeletonQuestionCard';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { SegmentedButton } from '@/components/ui/atoms/SegmentedButton';

type ModalityValue = 'spoken' | 'code-read-only' | 'code-write';
type ModalityLabelKey =
  | 'exam_config.modality.spoken'
  | 'exam_config.modality.code_review'
  | 'exam_config.modality.code_write';

const MODALITY_CONFIG: {
  value: ModalityValue;
  labelKey: ModalityLabelKey;
  icon: typeof Mic;
}[] = [
  { value: 'spoken', labelKey: 'exam_config.modality.spoken', icon: Mic },
  { value: 'code-read-only', labelKey: 'exam_config.modality.code_review', icon: Eye },
  { value: 'code-write', labelKey: 'exam_config.modality.code_write', icon: Code },
];

const QUESTION_DURATION_MIN = 1;
const QUESTION_DURATION_MAX = 30;

function parseQuestionDurationMinutes(raw: string): number {
  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) {
    return QUESTION_DURATION_MIN;
  }
  return Math.min(QUESTION_DURATION_MAX, Math.max(QUESTION_DURATION_MIN, parsed));
}

interface QuestionEditorProps {
  question: Question;
  index: number;
  skillName?: string;
  isLoading?: boolean;
  isAiGenerated?: boolean;
  isAssigningSkill?: boolean;
  isPerCandidate?: boolean;
  startInEditMode?: boolean;
  suggestedType?: string;  // LLM-recommended modality from assign-skill
  onUpdate: (updates: Partial<Question>) => void;
  onRemove: () => void;
  onRegenerate?: () => void;
  onTextBlur?: () => void;
}

export const QuestionEditor = ({
  question, index, skillName, isLoading, isAiGenerated, isAssigningSkill, isPerCandidate,
  startInEditMode, suggestedType, onUpdate, onRemove, onRegenerate, onTextBlur,
}: QuestionEditorProps) => {
  const { t } = useTranslation('job');
  const [isEditing, setIsEditing] = useState(startInEditMode ?? false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const modalities = useMemo(
    () =>
      MODALITY_CONFIG.map((mod) => ({
        ...mod,
        label: t(mod.labelKey),
      })),
    [t],
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  if (isLoading) {
    return (
      <div ref={setNodeRef} style={style}>
        <SkeletonQuestionCard />
      </div>
    );
  }

  const isCodeType = question.type === 'code-read-only' || question.type === 'code-write';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border p-3 transition-colors ${
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-purple/30' : ''
      } ${
        isPerCandidate
          ? 'bg-white border-dark-invirtus/10'
          : 'bg-white border-dark-invirtus/10'
      }`}
    >
      {/* Row 1: Controls */}
      <div className="flex items-center flex-wrap gap-2 mb-2.5">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-background-gray rounded text-dark-invirtus/40 hover:text-dark-invirtus/60 transition-colors"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <Tag intent="secondary" palette="standard" variant="solid" className="font-heading shrink-0">
          #{index + 1}
        </Tag>

        {skillName ? (
          <SkillChip
            label={skillName}
            className="shrink-0 cursor-default"
          />
        ) : isAssigningSkill ? (
          <Tag intent="secondary" variant="subtle" className="shrink-0 animate-pulse" icon={<Sparkles />}>
            {t('exam_config.question.assigning_skill')}
          </Tag>
        ) : (
          <Tag intent="secondary" variant="subtle" className="shrink-0 italic">
            {t('exam_config.question.no_skill_hint')}
          </Tag>
        )}

        {isPerCandidate && (
          <Tag
            intent="info"
            variant="subtle"
            className="font-heading shrink-0"
            title={t('exam_config.question.resume_tailored')}
            aria-label={t('exam_config.question.resume_tailored')}
            icon={<Sparkles />}
          >
            <span className="sr-only">{t('exam_config.question.resume_tailored')}</span>
          </Tag>
        )}

        {/* Modality selector */}
        <div className="ml-auto">
          <SegmentedButton
            aria-label={t('exam_config.modality.aria_label', {
              defaultValue: 'Question modality',
            })}
            size="sm"
            appearance="raised"
            intent="secondary"
            value={question.type}
            onValueChange={(value) => {
              const mod = value as ModalityValue;
              if (question.type === mod) return;
              const updates: Partial<Question> = { type: mod };
              if ((mod === 'code-read-only' || mod === 'code-write') && !question.codeSnippet) {
                updates.codeSnippet = '// Write your code here\n';
                updates.codingLanguage = 'typescript';
              }
              onUpdate(updates);
            }}
            options={modalities.map((mod) => {
              const Icon = mod.icon;
              return {
                value: mod.value,
                title: mod.label,
                disabled: isPerCandidate,
                icon: <Icon />,
                label: (
                  <span className="sr-only lg:not-sr-only lg:inline">{mod.label}</span>
                ),
              };
            })}
          />
        </div>

        {/* Duration input — adornments inside the Input border */}
        <div className="group/duration relative w-22 shrink-0 transition-[width] hover:w-[6.5rem] focus-within:w-[6.5rem] has-[:disabled]:hover:w-22 has-[:disabled]:focus-within:w-22">
          <Input
            type="number"
            min={QUESTION_DURATION_MIN}
            max={QUESTION_DURATION_MAX}
            value={question.timeMinutes}
            onChange={(e) => onUpdate({ timeMinutes: parseQuestionDurationMinutes(e.target.value) })}
            disabled={isPerCandidate}
            size="sm"
            className="tabular-nums text-center !pl-6 !pr-[2.125rem] sm:!pr-[2.25rem] [&::-webkit-inner-spin-button]:my-auto [&::-webkit-inner-spin-button]:h-7 [&::-webkit-inner-spin-button]:w-3 [&::-webkit-inner-spin-button]:cursor-pointer [&::-webkit-inner-spin-button]:hidden group-hover/duration:[&::-webkit-inner-spin-button]:!block group-focus-within/duration:[&::-webkit-inner-spin-button]:!block group-has-[:disabled]/duration:[&::-webkit-inner-spin-button]:!hidden"
          />
          <Clock
            aria-hidden
            className="pointer-events-none absolute left-2 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-dark-invirtus/40 group-has-[:disabled]/duration:opacity-50"
          />
          <span className="pointer-events-none absolute right-[0.75rem] top-1/2 z-10 -translate-y-1/2 text-xs font-body text-dark-invirtus/50 group-has-[:disabled]/duration:opacity-50">
            {t('exam_config.question.min')}
          </span>
        </div>

        {/* Action buttons */}
        {isAiGenerated && onRegenerate && !isPerCandidate && (
          <Button
            type="icon"
            intent="secondary"
            size="sm"
            icon={<RefreshCw/>}
            onClick={onRegenerate}
            title={t('exam_config.question.regenerate_aria')}
          />
        )}

        <Button
          type="icon"
          intent="danger"
          size="sm"
          icon={<Trash2/>}
          onClick={() => setShowDeleteConfirm(true)}
          title={t('exam_config.question.delete_aria')}
        />
      </div>

      {/* Row 2: Question text — read-only by default, click to edit */}
      {isPerCandidate ? (
        <div className="flex items-start gap-2 px-2.5 py-2 bg-background-gray/40 rounded-md border border-dark-invirtus/5">
          <Sparkles className="w-3.5 h-3.5 text-dark-invirtus/25 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-body text-dark-invirtus/60 leading-relaxed">
              {question.text || t('exam_config.question.ai_placeholder')}
            </p>
            <p className="text-[10px] font-body text-dark-invirtus/35 mt-1 italic">
              {t('exam_config.question.example_note')}
            </p>
          </div>
        </div>
      ) : isEditing ? (
        <Textarea
          autoFocus
          value={question.text}
          onChange={(e) => onUpdate({ text: e.target.value })}
          onBlur={() => {
            setIsEditing(false);
            if (onTextBlur && question.text.trim()) {
              onTextBlur();
            }
          }}
          placeholder={t('exam_config.question.enter_placeholder')}
          className="text-sm font-body border-dark-invirtus/10 focus-visible:ring-purple min-h-[60px] resize-y"
          rows={2}
        />
      ) : (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="w-full text-left group"
        >
          <div className="flex items-start gap-2 px-2.5 py-2 rounded-md border border-dark-invirtus/5 hover:border-dark-invirtus/15 hover:bg-background-gray/50 transition-colors">
            {question.text ? (
              <p className="text-sm font-body text-dark-invirtus leading-relaxed flex-1">
                {question.text}
              </p>
            ) : (
              <p className="text-sm font-body text-dark-invirtus/30 italic flex-1">
                {t('exam_config.question.click_to_add')}
              </p>
            )}
            <Pencil className="w-3.5 h-3.5 text-dark-invirtus/20 group-hover:text-dark-invirtus/40 mt-0.5 shrink-0 transition-colors" />
          </div>
        </button>
      )}

      {/* Modality mismatch banner — driven by LLM suggestion from assign-skill */}
      {suggestedType && suggestedType !== question.type && (() => {
        const labelKey: ModalityLabelKey =
          suggestedType === 'code-write'
            ? 'exam_config.modality.code_write'
            : suggestedType === 'code-read-only'
              ? 'exam_config.modality.code_review'
              : 'exam_config.modality.spoken';
        const label = t(labelKey);
        const Icon = suggestedType === 'code-write' ? Code : suggestedType === 'code-read-only' ? Eye : Mic;
        return (
          <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200/70 text-amber-700 text-xs font-body">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-500" />
            <span className="flex-1">
              <Trans
                ns="job"
                i18nKey="exam_config.question.modality_mismatch"
                values={{ label }}
                components={{ strong: <strong /> }}
              />
            </span>
            <Button
              type="outlined"
              intent="warning"
              size="xs"
              onClick={() => {
                const updates: Partial<Question> = { type: suggestedType as Question['type'] };
                if ((suggestedType === 'code-read-only' || suggestedType === 'code-write') && !question.codeSnippet) {
                  updates.codeSnippet = '// Write your code here\n';
                  updates.codingLanguage = 'typescript';
                }
                onUpdate(updates);
              }}
              icon={<Icon />}
              label={t('exam_config.question.switch')}
              className="bg-amber-100 hover:bg-amber-200 border-amber-300 text-amber-800 font-semibold"
            />
          </div>
        );
      })()}

      {/* Row 3: Code editor (conditional) */}
      {isCodeType && (
        <div className="mt-2.5">
          <MonacoIDE
            questionIndex={index}
            questionKind={question.type}
            isReadOnly={false}
            initialCode={question.codeSnippet || ''}
            language={question.codingLanguage || 'typescript'}
            onCodeChange={(code: string) => onUpdate({ codeSnippet: code })}
            onLanguageChange={(lang: string) => onUpdate({ codingLanguage: lang })}
            showLanguageSelector={true}
          />
        </div>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={t('exam_config.question.delete_title')}
        message={t('exam_config.question.delete_message')}
        closeLabel={t('exam_config.actions.close')}
        buttons={[
          {
            label: t('exam_config.actions.cancel'),
            onClick: () => setShowDeleteConfirm(false),
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label: t('exam_config.actions.delete'),
            onClick: () => {
              setShowDeleteConfirm(false);
              onRemove();
            },
            intent: 'danger',
          },
        ]}
      />
    </div>
  );
};
